import { IntlShape } from 'react-intl';
import {
    ServiceStatisticsFilter,
    ServiceStatisticsGranularity,
    ServiceStatisticsMetricsDTO,
    ServiceStatisticsServiceDTO,
    WaitCauseCode,
    WaitCauseDTO,
} from '../../dto/ServiceStatisticsDTO';
import { isoDate, parseIsoDate, TimelinePoint } from './statisticsUtils';

/** Un período que se mira en el panel de detalle: el punto del gráfico y cómo estaba agrupado. */
export interface PeriodTarget {
    point: TimelinePoint;
    granularity: ServiceStatisticsGranularity;
}

/**
 * El rango que se consulta para un período: los días del período que están dentro de lo que eligió el usuario. Las semanas
 * y los meses de los extremos se dibujan enteros, pero el backend solo cuenta los días del rango: así el detalle coincide
 * con el valor del punto.
 */
export function periodRange(point: TimelinePoint, rangeFrom: string, rangeTo: string): { from: string; to: string } {
    const from = new Date(Math.max(point.start.getTime(), parseIsoDate(rangeFrom).getTime()));
    const to = new Date(Math.min(point.end.getTime(), parseIsoDate(rangeTo).getTime()));
    return { from: isoDate(from), to: isoDate(to) };
}

/** Filtros del período: los de la pantalla con las fechas del período. */
export function periodFilter(base: ServiceStatisticsFilter, from: string, to: string): ServiceStatisticsFilter {
    return { ...base, initialDate: from, finalDate: to };
}

/**
 * Lo que se mira en el panel: un período (con los números de todo lo filtrado) o, si `serviceId` no es cero, ese servicio
 * o agenda en ese período (con los números del servicio). `granularity` es la del período.
 */
export interface PeriodView extends PeriodTarget {
    serviceId: number;
    serviceName: string;
}

/** Un día suelto de una semana o un mes: el mismo formato de punto del gráfico, para abrirlo en el mismo panel. */
export function dayPoint(day: Date, attentions: number, avgWait: number | null, avgService: number | null): TimelinePoint {
    return { period: isoDate(day), start: day, end: day, attentions, avgWait, avgService };
}

// ── Causas ──

/**
 * Las causas que comparan contra el período anterior no se muestran en el detalle: «el período anterior» de un día es el
 * día anterior, que puede ser un fin de semana, y la comparación no dice nada.
 */
const COMPARES_TO_PREVIOUS: WaitCauseCode[] = ['ATTENTIONS_DROP', 'WAIT_UP', 'MORE_PATIENTS', 'HIGHER_SEVERITY', 'LONGER_CONSULTS'];

export const withoutPreviousComparisons = (causes: WaitCauseDTO[]): WaitCauseDTO[] =>
    causes.filter(cause => !COMPARES_TO_PREVIOUS.includes(cause.code));

// ── Atención sin cerrar ──

/** Una espera más larga que esto, y que el triple del percentil 90, es más probable que sea una atención que no se cerró. */
export const SUSPECT_WAIT_MINUTES = 6 * 60;
export const SUSPECT_P90_FACTOR = 3;

/** Una atención de dos horas o más es probablemente una que no se cerró: la señal «Atención muy larga» de la lista de esperas. */
export const LONG_SERVICE_MINUTES = 120;

export interface OpenAttentionNotice {
    /** La espera más larga, en minutos. */
    maxWaitMinutes: number;
    /** La espera media sin esa atención. */
    averageWithoutIt: number;
}

/**
 * Si una sola atención explica la espera media: más de 6 h y más de 3 veces el percentil 90. Sin ella, el promedio es
 * (media × n − máxima) / (n − 1). Con una sola atención no hay con qué comparar.
 */
export function openAttentionNotice(metrics: ServiceStatisticsMetricsDTO): OpenAttentionNotice | null {
    const { attentions, avgWaitMinutes, maxWaitMinutes, waitP90Minutes } = metrics;
    if (attentions < 2) return null;
    if (!(maxWaitMinutes > SUSPECT_WAIT_MINUTES && maxWaitMinutes > SUSPECT_P90_FACTOR * waitP90Minutes)) return null;
    return {
        maxWaitMinutes,
        averageWithoutIt: Math.max(0, (avgWaitMinutes * attentions - maxWaitMinutes) / (attentions - 1)),
    };
}

/** «31 h 40 min» o, de menos de una hora, «45 min». */
export function formatDuration(minutes: number): string {
    const total = Math.round(minutes);
    const hours = Math.floor(total / 60);
    return hours > 0 ? `${hours} h ${String(total % 60).padStart(2, '0')} min` : `${total} min`;
}

// ── A qué hora ──

/** «08:30» → «08:30»; «08:00» → «08» (las horas en punto, sin los minutos: «08–12»). */
const shortClock = (time: string): string => (time.endsWith(':00') ? time.slice(0, 2) : time);

/** El horario programado: «08–12» (o «08:30–12» si no empieza en punto). */
export const scheduledRange = (from: string, to: string): string => `${shortClock(from)}–${shortClock(to)}`;

/** Lo que pasó de verdad, con los minutos: «08:10–13:43». */
export const realRange = (from: string, to: string): string => `${from}–${to}`;

/** «Dom 14/09»: el día corto de una fila de «Por día». */
export function shortDayLabel(intl: IntlShape, day: Date): string {
    const weekday = intl.formatDate(day, { weekday: 'short' }).replace('.', '');
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${intl.formatDate(day, { day: '2-digit', month: '2-digit' })}`;
}

// ── Dónde se esperó ──

/**
 * Cuándo una espera es «muy distinta de lo habitual» del servicio: al menos un 50 % más (el tag de atención) o un 100 % más
 * (el crítico), con al menos 10 atenciones. Un solo lugar para cambiar la regla.
 */
export const UNUSUAL_WAIT_RATIO = 0.5;
export const CRITICAL_WAIT_RATIO = 1;
export const UNUSUAL_WAIT_MIN_ATTENTIONS = 10;

export interface ServiceWaitRow {
    service: ServiceStatisticsServiceDTO;
    /** Espera media del servicio en todo el período de la pantalla; null si no hay dato. */
    usual: number | null;
    /** Minutos de espera de más que lo habitual: (espera del período − habitual) × atenciones; null sin habitual. */
    extraMinutes: number | null;
    /** Cuánto más (o menos) que lo habitual, en fracción (3,89 = 389 % más); null sin habitual o con habitual cero. */
    change: number | null;
    /** La espera es muy distinta de lo habitual (`warn` o `err`); null si no. */
    unusual: 'warn' | 'err' | null;
}

/**
 * Los servicios del período ordenados por cuánto se esperó de más que lo habitual (de mayor a menor), así una agenda que
 * esperó 230 min contra 59 habituales no queda perdida tercera. Los que no tienen habitual van después, por atenciones con
 * más de una hora de espera y luego por atenciones.
 */
export function rankServices(services: ServiceStatisticsServiceDTO[], usualWait: Map<number, number>): ServiceWaitRow[] {
    const rows = services.map<ServiceWaitRow>(service => {
        const usual = usualWait.get(service.serviceId) ?? null;
        const { attentions, avgWaitMinutes } = service.current;
        const change = usual !== null && usual > 0 ? (avgWaitMinutes - usual) / usual : null;
        const unusual = change !== null && attentions >= UNUSUAL_WAIT_MIN_ATTENTIONS && change >= UNUSUAL_WAIT_RATIO
            ? (change >= CRITICAL_WAIT_RATIO ? 'err' : 'warn') : null;
        return {
            service, usual, change, unusual,
            extraMinutes: usual === null ? null : (avgWaitMinutes - usual) * attentions,
        };
    });
    return rows.sort((a, b) => {
        if (a.extraMinutes !== null && b.extraMinutes !== null) return b.extraMinutes - a.extraMinutes;
        if (a.extraMinutes !== null) return -1;
        if (b.extraMinutes !== null) return 1;
        return b.service.current.waitOver60 - a.service.current.waitOver60 || b.service.current.attentions - a.service.current.attentions;
    });
}

// ── Atenciones con más espera ──

/** Las partes de una hora local sin zona (`yyyy-MM-ddTHH:mm:ss`): se leen del texto, nunca con `new Date(texto)`. */
export function splitLocalTime(text: string): { date: string; time: string } {
    return { date: text.slice(0, 10), time: text.slice(11, 16) };
}

/** «URGENCIA» → «Urgencia»: los códigos de atención se cargan en mayúsculas y en una línea se leen mal. */
export function readablePriority(name: string): string {
    return name === name.toUpperCase() ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : name;
}
